<template>
  <section
    v-show="!isRegistrationTrigger"
    :class="$style.sectionRoot"
  >
    <header :class="$style.sectionHeader">
      Launch Event
    </header>
    <div :class="$style.sectionContent">
      <div>
        <DropDown
          attribute="launchType"
          :class="$style.launchTypeDropdown"
          label="Event"
          name="launch type"
          optionLabelKey="name"
          :options="launchTypeOptions"
          optionValueKey="value"
          :value="item.launchType"
          @input="handleFieldChange"
        />
      </div>
      <div v-show="item.launchType === launchTypes.oneTime">
        <span :class="$style.inlineLabel">
          Schedule run for:
        </span>
        <datepicker
          :class="[$style.input, $style.scheduledRunDateInput]"
          :format="dateFormat"
          name="scheduled run date"
          placeholder="YYYY/MM/DD"
          :typeable="true"
          :value="scheduledRunDate"
          @input="date => setScheduledRunDateTime({ date: date || new Date() })"
        />
        <span :class="$style.inlineLabel">
          at
        </span>
        <InputValidator
          :class="[$style.input, $style.scheduledRunTimeInput]"
          :config="{
            label: '',
            name: 'scheduled run time',
            placeholder: 'HH:MM',
            validate: item.launchType === launchTypes.oneTime ? 'required' : '',
            value: scheduledRunTime,
          }"
          @InputValidatorBlur="value => setScheduledRunDateTime({ time: value })"
        />
        <TooltipIcon
          :class="$style.scheduledRunTooltip"
          hasIndicator
          icon="info"
          :isDark="false"
          :isMarkdown="false"
          :linkHref="kbArticleGettingStartedCommands"
          :linkText="$options.timeInputTooltip.linkText"
          orientation="left"
          :text="$options.timeInputTooltip.text"
        />
      </div>
      <div v-show="item.launchType === launchTypes.repeated">
        <DropDown
          attribute="scheduleRepeatType"
          :class="[$style.input, $style.repeatOptionsInput]"
          label="Command repeats by"
          name="repeat type"
          optionLabelKey="name"
          :options="repeatTypeOptions"
          optionValueKey="value"
          :value="item.scheduleRepeatType"
          @input="handleFieldChange"
        />
        <div :class="$style.repeatOptionsRow">
          <div v-show="showRepeatEveryInput">
            <InputValidator
              :class="[$style.input, $style.repeatOptionsInput, $style.repeatEveryInput]"
              :config="{
                label: 'Repeat every',
                max: repeatEveryInputMax,
                min: 1,
                name: 'repeatEvery',
                type: 'number',
                validate: repeatEveryInputValidation,
                value: repeatEvery,
              }"
              @InputValidatorBlur="value => setRepeatingSchedule({ repeatEvery: value })"
            />
            <span :class="$style.inlineLabelWithInputLabel">
              {{ item.scheduleRepeatType.toLowerCase() }}s
            </span>
          </div>
          <OptionalValidationWrapper
            v-show="item.scheduleRepeatType === repeatTypes.week"
            :class="$style.repeatOnDaysContainer"
            :clickPrevent="false"
            label="Repeat on"
          >
            <DropDownMultiSelectSearch
              :class="$style.repeatOnDaysDropdown"
              emptyValueText="none"
              name="Days"
              :options="repeatOnDaysOptions"
              :showFullValueText="true"
              :value="repeatOnDays"
              @input="value => setRepeatingSchedule({ repeatOnDays: value })"
            />
          </OptionalValidationWrapper>
          <div v-show="item.scheduleRepeatType === repeatTypes.month">
            <DropDown
              :class="[$style.input, $style.repeatEveryMonthInput]"
              label="Repeat every"
              name="repeat by month"
              optionLabelKey="name"
              :options="monthOptions"
              optionValueKey="value"
              :value="repeatEvery"
              @input="value => setRepeatingSchedule({ repeatEvery: value })"
            />
            <InputValidator
              :class="[$style.input, $style.repeatEveryMonthOnDayInput]"
              :config="{
                label: 'On day',
                max: 31,
                min: 1,
                name: 'repeat every month on day',
                type: 'number',
                validate: item.launchType === launchTypes.repeated
                  && item.scheduleRepeatType === repeatTypes.month ? 'required' : '',
                value: repeatEveryMonthOnDay,
              }"
              @InputValidatorBlur="value => setRepeatingSchedule({ repeatEveryMonthOnDay: value })"
            />
          </div>
          <InputValidator
            v-show="showRunAtInput"
            :class="$style.repeatOptionsInput"
            :config="{
              label: 'Run at',
              name: 'run at time',
              placeholder: 'HH:MM',
              validate: item.launchType === launchTypes.repeated
                && showRunAtInput ? 'required' : '',
              value: runAtTime,
            }"
            @InputValidatorBlur="value => setRepeatingSchedule({ runAtTime: value })"
          />
          <TooltipIcon
            :class="$style.scheduledRunTooltip"
            hasIndicator
            icon="info"
            :isDark="false"
            :isMarkdown="false"
            :linkHref="kbArticleGettingStartedCommands"
            :linkText="$options.timeInputTooltip.linkText"
            orientation="left"
            :text="$options.timeInputTooltip.text"
          />
        </div>
      </div>
      <div v-show="item.launchType === launchTypes.trigger">
        <InputValidator
          :config="{
            attribute: 'trigger',
            label: 'Trigger Name',
            name: 'trigger',
            validate: item.launchType === launchTypes.trigger ? 'required' : '',
            value: item.trigger,
          }"
          :tooltip="{
            text: 'Learn more about triggers from our Knowledge Base',
            kbLink: kbArticleCommandTriggers,
            kbLinkText: 'Knowledge Base'
          }"
          @InputValidatorModel="handleFieldChange"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { TooltipIcon } from '@jumpcloud/ui-components';
import { mapActions, mapGetters, mapState } from 'vuex';
import Datepicker from 'vuejs-datepicker';
import DropDown from 'components/FormValidation/DropDown.vue';
import DropDownMultiSelectSearch from 'components/DropDownMenu/DropDownMultiSelectSearch/DropDownMultiSelectSearch.vue';
import FeatureTrialsMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';
import InputValidator from 'components/FormValidation/InputValidator.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import OptionalValidationWrapper from 'components/FormValidation/OptionalValidationWrapper.vue';
import launchTypes from 'client/Commands/constants/LaunchTypes';
import moment from 'moment';
import repeatTypes from 'client/Commands/constants/RepeatTypes';

const commandModel = StoreModelMap[ItemTypeConsts.command];

export default {
  name: 'CommandDetailsLaunchEvent',

  inject: ['$validator'],

  components: {
    Datepicker,
    DropDown,
    DropDownMultiSelectSearch,
    InputValidator,
    OptionalValidationWrapper,
    TooltipIcon,
  },

  mixins: [
    FeatureTrialsMixin,
  ],

  props: {
    isRegistrationTrigger: Boolean,
  },

  data() {
    const monthOptions = [];
    for (let i = 1; i <= 12; i += 1) {
      monthOptions.push({
        name: moment(i.toString(), 'MM').format('MMMM'),
        value: String(i),
      });
    }
    return {
      dateFormat: 'yyyy/MM/dd',
      kbArticleCommandTriggers: KbArticleUrls.commandTriggers,
      kbArticleGettingStartedCommands: KbArticleUrls.gettingStartedCommands,
      launchTypes,
      monthOptions,
      repeatTypeOptions: [
        { name: 'Minute', value: repeatTypes.minute },
        { name: 'Hour', value: repeatTypes.hour },
        { name: 'Day', value: repeatTypes.day },
        { name: 'Week', value: repeatTypes.week },
        { name: 'Month', value: repeatTypes.month },
      ],
      repeatOnDaysOptions: [
        { name: 'Sunday', value: 'Sunday' },
        { name: 'Monday', value: 'Monday' },
        { name: 'Tuesday', value: 'Tuesday' },
        { name: 'Wednesday', value: 'Wednesday' },
        { name: 'Thursday', value: 'Thursday' },
        { name: 'Friday', value: 'Friday' },
        { name: 'Saturday', value: 'Saturday' },
      ],
      repeatTypes,
    };
  },

  timeInputTooltip: {
    linkText: 'Getting Started: Commands',
    text: 'Commands run at the time you specify according to the device’s local time zone. For more information, see:',
  },

  computed: {
    ...mapGetters(commandModel, [
      'repeatEvery',
      'repeatEveryMonthOnDay',
      'repeatOnDays',
      'runAtTime',
      'scheduledRunDate',
      'scheduledRunTime',
    ]),

    ...mapState(commandModel, [
      'item',
    ]),

    launchTypeOptions() {
      if (this.$hasFeatureFlag(this.$featureFlags.commandTriggerLogin)) {
        return [
          { name: 'Run Manually', value: launchTypes.manual },
          { name: 'Run as Scheduled', value: launchTypes.oneTime },
          { name: 'Run as Repeating', value: launchTypes.repeated },
          { name: 'Run on Trigger (webhook)', value: launchTypes.trigger },
          { name: 'Run on Every JumpCloud Login', value: launchTypes.everyLogin },
          { name: 'Run on Next JumpCloud Login', value: launchTypes.nextLogin },
        ];
      }
      return [
        { name: 'Run Manually', value: launchTypes.manual },
        { name: 'Run as Scheduled', value: launchTypes.oneTime },
        { name: 'Run as Repeating', value: launchTypes.repeated },
        { name: 'Run on Trigger (webhook)', value: launchTypes.trigger },
      ];
    },

    repeatEveryInputMax() {
      switch (this.item.scheduleRepeatType) {
        case repeatTypes.minute:
          return 59;
        case repeatTypes.hour:
          return 23;
        case repeatTypes.month:
          return 31;
        default:
          return null;
      }
    },

    repeatEveryInputValidation() {
      let validation = 'min_value:1';
      if (this.repeatEveryInputMax) {
        validation += `|max_value:${this.repeatEveryInputMax}`;
      }
      if (this.item.launchType === launchTypes.repeated && this.showRepeatEveryInput) {
        validation += '|required';
      }
      return validation;
    },

    showRepeatEveryInput() {
      return [repeatTypes.minute, repeatTypes.hour, repeatTypes.day]
        .includes(this.item.scheduleRepeatType);
    },

    showRunAtInput() {
      return [repeatTypes.day, repeatTypes.week, repeatTypes.month]
        .includes(this.item.scheduleRepeatType);
    },
  },

  created() {
    // Add custom validation error messages
    this.$validator.localize('en', {
      custom: {
        repeatEvery: {
          min_value: 'This must be at least 1',
          max_value: (rule, [value]) => `This must be less than ${+value + 1}`,
          required: 'This field is required',
        },
      },
    });

    if (this.isRegistrationTrigger) {
      this.updateItem({
        ...this.item,
        launchType: launchTypes.nextRegistration,
      });
    }
  },

  methods: {
    ...mapActions(commandModel, [
      'setItemField',
      'setRepeatingSchedule',
      'setScheduledRunDateTime',
      'updateItem',
    ]),

    handleFieldChange(value, key) {
      this.setItemField({ key, value });
    },
  },
};
</script>

<style module>
@import '../../css/sections.css';

.launchTypeDropdown { width: 300px; }
.launchTypeDropdown:focus { outline: none; }

.input {
  display: inline-block;
  vertical-align: top;
}

.input input {
  background: var(--jcWhite);
  border: 2px solid var(--jcGrey200);
  border-radius: 2px;
  line-height: 1.5;
  outline: 0;
  padding: 6px 9px 4px;
  transition: border-color var(--jc-transition-duration) ease-in-out;
}

.scheduledRunDateInput input { width: 110px; }

.scheduledRunTimeInput,
.scheduledRunTooltip {
  align-self: center;
  display: inline-block;
  width: unset;
}

.scheduledRunTimeInput input { width: 80px !important; } /* overriding scoped Input.vue styles */

.scheduledRunDateInput,
.scheduledRunTimeInput {
  margin: 0 6px;
}

.repeatOptionsRow {
  display: flex;
  justify-content: flex-start;
}

.repeatOptionsRow > * { margin-right: 40px; }
.repeatOptionsInput { width: 180px; }

.repeatEveryInput {
  margin-right: 6px;
  width: 100px;
}

.repeatOnDaysContainer { width: auto; }
.repeatOnDaysDropdown { margin-top: 3px; }
.repeatEveryMonthInput { width: 180px; }
.repeatEveryMonthOnDayInput { width: 100px; }

.inlineLabel {
  position: relative;
  top: 7px;
}

.inlineLabelWithInputLabel {
  position: relative;
  top: 34px;
}
</style>
